/**
 * Dependency installer: auto-install all required deps via detected package manager.
 * Splits into core (production) and dev dependencies.
 * Preset and integration deps are layered on top of the core dependency plan.
 * Uses async spawn so the clack spinner can animate during install.
 */

import { spawn } from 'node:child_process'
import fs from 'node:fs'
import { createRequire } from 'node:module'
import path from 'node:path'
import { getCurrentCliVersion } from '@core-engine/snapshots/store.js'
import type { PackageManager } from '@core-engine/utils/package-manager.js'
import type { BetterstartIntegrationId } from '@integration-engine/types.js'
import { NEXT_ADAPTER_DEPS, NEXT_ADAPTER_DEV_DEPS } from '@next-adapter/dependencies.js'
import { getIntegrationDefinition } from '@next-adapter/integration-registry.js'
import { getPresetDefinition } from '@next-adapter/preset-registry.js'
import type { BetterstartPresetId } from '@preset-engine/types.js'
import { REACT_ADMIN_DEPS, REACT_ADMIN_DEV_DEPS } from '@shared-assets/react-admin/dependencies.js'

export interface InstallDepsOptions {
  cwd: string
  pm: PackageManager
  dependencies: string[]
  devDependencies: string[]
}

export interface InstallDepsResult {
  dependencies: string[]
  devDeps: string[]
  success: boolean
  error: string | null
}

export interface DependencyPlan {
  dependencies: string[]
  devDependencies: string[]
}

interface ProjectPackageJson {
  dependencies?: Record<string, string>
  devDependencies?: Record<string, string>
  optionalDependencies?: Record<string, string>
  pnpm?: {
    packageExtensions?: Record<string, PnpmPackageExtension>
  }
}

type DependencySection = 'dependencies' | 'devDependencies'
type PnpmPackageExtension = {
  dependencies?: Record<string, string>
  peerDependencies?: Record<string, string>
}

const CLI_PACKAGE_NAME = 'betterstart-cli'
const LOCAL_PACKAGE_PREFIXES = ['workspace:', 'link:', 'file:', 'portal:']
const TIPTAP_PACKAGE_PREFIX = '@tiptap/'
const TIPTAP_PACKAGE_VERSION_SPEC = '^3.26.0'
const CODEMIRROR_PACKAGE_SPECS: Record<string, string> = {
  '@codemirror/commands': '^6.10.3',
  '@codemirror/lang-javascript': '^6.2.5',
  '@codemirror/lang-json': '^6.0.2',
  '@codemirror/lang-markdown': '^6.5.0',
  '@codemirror/state': '^6.6.0',
  '@codemirror/view': '^6.43.0',
  '@uiw/codemirror-theme-github': '^4.25.10',
  '@uiw/react-codemirror': '^4.25.10'
}
const requireFromCli = createRequire(import.meta.url)
interface PnpmPackageExtensionDefinition {
  packageName: string
  selector: string
  extension: PnpmPackageExtension & {
    dependencies: Record<string, string>
  }
}

const PNPM_PACKAGE_EXTENSION_DEFINITIONS: PnpmPackageExtensionDefinition[] = [
  {
    packageName: '@uiw/codemirror-theme-github',
    selector: '@uiw/codemirror-theme-github@*',
    extension: {
      dependencies: {
        '@babel/runtime': '>=7.11.0',
        '@lezer/highlight': '*'
      }
    }
  }
]

// ============================================================================
// Dependency Lists
// ============================================================================

/** Core runtime deps — always installed */
export const CORE_DEPS = Array.from(new Set([...NEXT_ADAPTER_DEPS, ...REACT_ADMIN_DEPS]))

/** Dev deps — always installed */
export const DEV_DEPS = Array.from(new Set([...NEXT_ADAPTER_DEV_DEPS, ...REACT_ADMIN_DEV_DEPS]))

/** Biome dev dep — only when no existing linter */
export const BIOME_DEV_DEPS = ['@biomejs/biome']

export const POSTGRES_RUNTIME_DEP = 'postgres'
export const DRIZZLE_KIT_POSTGRES_DRIVER_DEP = 'pg'

function readProjectPackageJson(cwd: string): ProjectPackageJson | null {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) {
    return null
  }

  try {
    return JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as ProjectPackageJson
  } catch {
    return null
  }
}

function dependencySpecMatches(spec: string, name: string): boolean {
  return spec === name || spec.startsWith(`${name}@`)
}

function packageNameFromSpec(spec: string): string {
  if (spec.startsWith('@')) {
    const versionSeparator = spec.indexOf('@', 1)
    return versionSeparator === -1 ? spec : spec.slice(0, versionSeparator)
  }

  const versionSeparator = spec.indexOf('@')
  return versionSeparator === -1 ? spec : spec.slice(0, versionSeparator)
}

function getPnpmPackageExtensionsToEnsure(
  pkg: ProjectPackageJson | null,
  dependencies: string[]
): Record<string, PnpmPackageExtension> {
  const extensions: Record<string, PnpmPackageExtension> = {}

  for (const definition of PNPM_PACKAGE_EXTENSION_DEFINITIONS) {
    const shouldEnsure =
      dependencies.some((dependency) =>
        dependencySpecMatches(dependency, definition.packageName)
      ) || hasDeclaredPackage(pkg, definition.packageName)

    if (shouldEnsure) {
      extensions[definition.selector] = definition.extension
    }
  }

  return extensions
}

function ensurePnpmPackageExtensions(cwd: string, dependencies: string[]): void {
  const pkgPath = path.join(cwd, 'package.json')
  const pkg = readProjectPackageJson(cwd)
  if (!pkg) {
    return
  }

  const extensionsToEnsure = getPnpmPackageExtensionsToEnsure(pkg, dependencies)
  if (Object.keys(extensionsToEnsure).length === 0) {
    return
  }

  let changed = false
  const pnpmConfig = pkg.pnpm ?? {}
  const packageExtensions = pnpmConfig.packageExtensions ?? {}

  for (const [selector, extension] of Object.entries(extensionsToEnsure)) {
    const currentExtension = packageExtensions[selector] ?? {}
    const currentDependencies = currentExtension.dependencies ?? {}
    const nextDependencies = { ...currentDependencies }

    for (const [name, version] of Object.entries(extension.dependencies ?? {})) {
      if (nextDependencies[name] !== version) {
        nextDependencies[name] = version
        changed = true
      }
    }

    packageExtensions[selector] = {
      ...currentExtension,
      dependencies: nextDependencies
    }
  }

  if (!changed && pkg.pnpm?.packageExtensions) {
    return
  }

  pkg.pnpm = {
    ...pnpmConfig,
    packageExtensions
  }
  fs.writeFileSync(pkgPath, `${JSON.stringify(pkg, null, 2)}\n`, 'utf-8')
}

function hasDeclaredPackage(pkg: ProjectPackageJson | null, name: string): boolean {
  if (!pkg) return false
  return (
    Object.hasOwn(pkg.dependencies ?? {}, name) ||
    Object.hasOwn(pkg.devDependencies ?? {}, name) ||
    Object.hasOwn(pkg.optionalDependencies ?? {}, name)
  )
}

function getDeclaredRuntimePackageNames(pkg: ProjectPackageJson | null): string[] {
  if (!pkg) return []
  return Object.keys({
    ...(pkg.dependencies ?? {}),
    ...(pkg.optionalDependencies ?? {})
  })
}

function getDeclaredDevPackageNames(pkg: ProjectPackageJson | null): string[] {
  if (!pkg) return []
  return Object.keys(pkg.devDependencies ?? {}).filter(
    (name) => !Object.hasOwn(pkg.dependencies ?? {}, name)
  )
}

function isTiptapPackageName(name: string): boolean {
  return name.startsWith(TIPTAP_PACKAGE_PREFIX)
}

function getAlignedPackageSpec(name: string): string | null {
  if (isTiptapPackageName(name)) {
    return `${name}@${TIPTAP_PACKAGE_VERSION_SPEC}`
  }

  const versionSpec = CODEMIRROR_PACKAGE_SPECS[name]
  return versionSpec ? `${name}@${versionSpec}` : null
}

function hasAlignedPackageSpec(spec: string): boolean {
  return getAlignedPackageSpec(packageNameFromSpec(spec)) !== null
}

function alignDependencySpecs(dependencies: string[]): string[] {
  return dependencies.map((dependency) => {
    const name = packageNameFromSpec(dependency)
    return getAlignedPackageSpec(name) ?? dependency
  })
}

function getAlignedDeclaredDependencies(packageNames: string[]): string[] {
  return packageNames.flatMap((name) => {
    const alignedSpec = getAlignedPackageSpec(name)
    return alignedSpec ? [alignedSpec] : []
  })
}

function resolveDependencyInstallPlan(options: InstallDepsOptions): InstallDepsOptions {
  const pkg = readProjectPackageJson(options.cwd)
  const dependencies = alignDependencySpecs(options.dependencies)
  const devDependencies = alignDependencySpecs(options.devDependencies)
  const needsPackageAlignment = [...options.dependencies, ...options.devDependencies].some(
    hasAlignedPackageSpec
  )

  if (!needsPackageAlignment) {
    return {
      ...options,
      dependencies: unique(dependencies),
      devDependencies: unique(devDependencies)
    }
  }

  return {
    ...options,
    dependencies: unique([
      ...dependencies,
      ...getAlignedDeclaredDependencies(getDeclaredRuntimePackageNames(pkg))
    ]),
    devDependencies: unique([
      ...devDependencies,
      ...getAlignedDeclaredDependencies(getDeclaredDevPackageNames(pkg))
    ])
  }
}

function canResolveFromProject(cwd: string, name: string): boolean {
  try {
    requireFromCli.resolve(name, { paths: [cwd] })
    return true
  } catch {
    return false
  }
}

export function hasProjectDependency(cwd: string, dependency: string): boolean {
  const pkg = readProjectPackageJson(cwd)
  return hasDeclaredPackage(pkg, dependency) && canResolveFromProject(cwd, dependency)
}

export function hasPostgresRuntimeDependency(cwd: string): boolean {
  return hasProjectDependency(cwd, POSTGRES_RUNTIME_DEP)
}

export function hasDrizzleKitPostgresDriverDependency(cwd: string): boolean {
  return hasProjectDependency(cwd, DRIZZLE_KIT_POSTGRES_DRIVER_DEP)
}

function getDeclaredCliDependency(
  pkg: ProjectPackageJson
): { section: DependencySection; spec: string } | null {
  const devDependency = pkg.devDependencies?.[CLI_PACKAGE_NAME]
  if (typeof devDependency === 'string' && devDependency.length > 0) {
    return { section: 'devDependencies', spec: devDependency }
  }

  const dependency = pkg.dependencies?.[CLI_PACKAGE_NAME]
  if (typeof dependency === 'string' && dependency.length > 0) {
    return { section: 'dependencies', spec: dependency }
  }

  return null
}

function isLocalPackageSpec(spec: string): boolean {
  return LOCAL_PACKAGE_PREFIXES.some((prefix) => spec.startsWith(prefix))
}

function matchesCurrentCliVersion(spec: string, currentVersion: string): boolean {
  return (
    spec === currentVersion ||
    spec === `^${currentVersion}` ||
    spec === `~${currentVersion}` ||
    spec === `=${currentVersion}`
  )
}

export function getCliDependencySyncPlan(cwd: string): DependencyPlan | null {
  const currentVersion = getCurrentCliVersion()
  if (!currentVersion || currentVersion === '0.0.0') {
    return null
  }

  const desiredDependency = `${CLI_PACKAGE_NAME}@${currentVersion}`
  const pkg = readProjectPackageJson(cwd)
  if (!pkg) {
    return {
      dependencies: [],
      devDependencies: [desiredDependency]
    }
  }

  const declaredDependency = getDeclaredCliDependency(pkg)
  if (!declaredDependency) {
    return {
      dependencies: [],
      devDependencies: [desiredDependency]
    }
  }

  if (
    isLocalPackageSpec(declaredDependency.spec) ||
    matchesCurrentCliVersion(declaredDependency.spec, currentVersion)
  ) {
    return null
  }

  return declaredDependency.section === 'dependencies'
    ? {
        dependencies: [desiredDependency],
        devDependencies: []
      }
    : {
        dependencies: [],
        devDependencies: [desiredDependency]
      }
}

// ============================================================================
// Installer
// ============================================================================

function buildAddArgs(pm: PackageManager, deps: string[], dev: boolean): string[] {
  const devFlag = dev ? (pm === 'yarn' ? '--dev' : '-D') : ''

  switch (pm) {
    case 'pnpm':
      return ['add', ...(devFlag ? [devFlag] : []), ...deps]
    case 'yarn':
      return ['add', ...(devFlag ? [devFlag] : []), ...deps]
    case 'bun':
      return ['add', ...(devFlag ? [devFlag] : []), ...deps]
    default:
      return ['install', ...(devFlag ? [devFlag] : []), ...deps]
  }
}

function buildRemoveArgs(pm: PackageManager, deps: string[], dev: boolean): string[] {
  const devFlag = dev ? (pm === 'yarn' ? '--dev' : '-D') : ''
  const packageNames = deps.map(packageNameFromSpec)

  switch (pm) {
    case 'pnpm':
      return ['remove', ...(devFlag ? [devFlag] : []), ...packageNames]
    case 'yarn':
      return ['remove', ...(devFlag ? [devFlag] : []), ...packageNames]
    case 'bun':
      return ['remove', ...(devFlag ? [devFlag] : []), ...packageNames]
    default:
      return ['uninstall', ...(devFlag ? [devFlag] : []), ...packageNames]
  }
}

function spawnAsync(cmd: string, args: string[], cwd: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const child = spawn(cmd, args, { cwd, stdio: 'pipe' })
    let stderr = ''
    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString()
    })
    child.on('close', (code) => {
      if (code === 0) resolve()
      else reject(new Error(stderr || `${cmd} exited with code ${code}`))
    })
    child.on('error', reject)
  })
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values))
}

export function getDependencyPlan(
  presetIds: BetterstartPresetId[],
  integrationIds: BetterstartIntegrationId[],
  includeBiome: boolean
): DependencyPlan {
  const presetDependencies = presetIds.flatMap(
    (presetId) => getPresetDefinition(presetId).packageDependencies
  )
  const presetDevDependencies = presetIds.flatMap(
    (presetId) => getPresetDefinition(presetId).devDependencies
  )
  const integrationDependencies = integrationIds.flatMap(
    (integrationId) => getIntegrationDefinition(integrationId).packageDependencies
  )
  const integrationDevDependencies = integrationIds.flatMap(
    (integrationId) => getIntegrationDefinition(integrationId).devDependencies
  )

  return {
    dependencies: unique([...CORE_DEPS, ...presetDependencies, ...integrationDependencies]),
    devDependencies: unique([
      ...DEV_DEPS,
      ...presetDevDependencies,
      ...integrationDevDependencies,
      ...(includeBiome ? BIOME_DEV_DEPS : [])
    ])
  }
}

export async function installDependenciesAsync({
  cwd,
  pm,
  dependencies,
  devDependencies
}: InstallDepsOptions): Promise<InstallDepsResult> {
  const installPlan = resolveDependencyInstallPlan({ cwd, pm, dependencies, devDependencies })

  try {
    if (pm === 'pnpm') {
      ensurePnpmPackageExtensions(cwd, installPlan.dependencies)
    }

    // Install core deps
    if (installPlan.dependencies.length > 0) {
      await spawnAsync(pm, buildAddArgs(pm, installPlan.dependencies, false), cwd)
    }

    // Install dev deps
    if (installPlan.devDependencies.length > 0) {
      await spawnAsync(pm, buildAddArgs(pm, installPlan.devDependencies, true), cwd)
    }

    return {
      dependencies: installPlan.dependencies,
      devDeps: installPlan.devDependencies,
      success: true,
      error: null
    }
  } catch (err) {
    return {
      dependencies: installPlan.dependencies,
      devDeps: installPlan.devDependencies,
      success: false,
      error: err instanceof Error ? err.message : 'Install failed'
    }
  }
}

export async function removeDependenciesAsync({
  cwd,
  pm,
  dependencies,
  devDependencies
}: InstallDepsOptions): Promise<InstallDepsResult> {
  try {
    if (dependencies.length > 0) {
      await spawnAsync(pm, buildRemoveArgs(pm, dependencies, false), cwd)
    }

    if (devDependencies.length > 0) {
      await spawnAsync(pm, buildRemoveArgs(pm, devDependencies, true), cwd)
    }

    return { dependencies, devDeps: devDependencies, success: true, error: null }
  } catch (err) {
    return {
      dependencies,
      devDeps: devDependencies,
      success: false,
      error: err instanceof Error ? err.message : 'Remove failed'
    }
  }
}

export async function syncProjectCliDependency(
  cwd: string,
  pm: PackageManager
): Promise<InstallDepsResult | null> {
  const dependencyPlan = getCliDependencySyncPlan(cwd)
  if (!dependencyPlan) {
    return null
  }

  return installDependenciesAsync({
    cwd,
    pm,
    dependencies: dependencyPlan.dependencies,
    devDependencies: dependencyPlan.devDependencies
  })
}
